import shaderSources from "@/layers/shaders/all";
import type { BinaryColormapRenderInfo } from "./BinaryColormapState";

enum BinaryDrapedMode {
  Grayscale = 0,
  Turbo = 1,
  LUT = 2,
}

export type BinaryDrapedDrawOptions = {
  tileTexture: WebGLTexture;
  texCoords: { texLeft: number; texRight: number; texTop: number; texBottom: number };
  /** NDC sub-region to render into. Defaults to the full tile [-1,1]×[-1,1]. */
  posCoords?: { posLeft: number; posBottom: number; posRight: number; posTop: number };
  opacity: number;
  colorMapName: string;
  parameterUnit: string;
  geoMinMax?: { min: number; max: number };
  colormapInfo: BinaryColormapRenderInfo;
  debugLoggingEnabled: boolean;
};

/**
 * Renders a single binary tile on the globe using the draped shader.
 * Handles colormap selection, opacity, and optional debug logging.
 */
export class BinaryDrapedTileRenderer {
  private program?: WebGLProgram;
  private posBuffer?: WebGLBuffer;
  private uvBuffer?: WebGLBuffer;
  private aPos?: number;
  private aUv?: number;
  private uPayload: WebGLUniformLocation | null = null;
  private uOpacity: WebGLUniformLocation | null = null;
  private uMode: WebGLUniformLocation | null = null;
  private uColormapSampler: WebGLUniformLocation | null = null;
  private uProps2: WebGLUniformLocation | null = null;

  private lastMode?: number;
  private lastColormapDebugKey?: string;

  init(gl: WebGL2RenderingContext): void {
    if (this.program) {
      return;
    }

    const vsSource = shaderSources("./binary/draped.vert").default;
    const fsSource = shaderSources("./binary/draped.frag").default;

    const compile = (type: number, src: string) => {
      const sh = gl.createShader(type);
      if (!sh) {
        throw new Error("Failed to create shader");
      }
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        const log = gl.getShaderInfoLog(sh) || "";
        gl.deleteShader(sh);
        throw new Error(log);
      }
      return sh;
    };

    const vs = compile(gl.VERTEX_SHADER, vsSource);
    const fs = compile(gl.FRAGMENT_SHADER, fsSource);

    const program = gl.createProgram();
    if (!program) {
      throw new Error("Failed to create program");
    }
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(program) || "";
      gl.deleteProgram(program);
      throw new Error(log);
    }

    this.program = program;
    this.aPos = gl.getAttribLocation(program, "a_position");
    this.aUv = gl.getAttribLocation(program, "a_texCoord");
    this.uPayload = gl.getUniformLocation(program, "u_payload");
    this.uOpacity = gl.getUniformLocation(program, "u_opacity");
    this.uMode = gl.getUniformLocation(program, "u_mode");
    this.uColormapSampler = gl.getUniformLocation(program, "u_colormapSampler");
    this.uProps2 = gl.getUniformLocation(program, "u_props2");

    const pos = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);
    // Binary tiles are south-up; flip V to keep north-up on screen.
    const uv = new Float32Array([0, 1, 1, 1, 0, 0, 0, 0, 1, 1, 1, 0]);

    this.posBuffer = gl.createBuffer() || undefined;
    if (this.posBuffer) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuffer);
      // DYNAMIC_DRAW: positions are updated per draw call for child tile sub-regions.
      gl.bufferData(gl.ARRAY_BUFFER, pos, gl.DYNAMIC_DRAW);
    }

    this.uvBuffer = gl.createBuffer() || undefined;
    if (this.uvBuffer) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.uvBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, uv, gl.STATIC_DRAW);
    }
  }

  dispose(gl: WebGL2RenderingContext): void {
    if (this.program) {
      gl.deleteProgram(this.program);
    }
    if (this.posBuffer) {
      gl.deleteBuffer(this.posBuffer);
    }
    if (this.uvBuffer) {
      gl.deleteBuffer(this.uvBuffer);
    }
    this.program = undefined;
    this.posBuffer = undefined;
    this.uvBuffer = undefined;
  }

  draw(gl: WebGL2RenderingContext, options: BinaryDrapedDrawOptions): void {
    if (
      !this.program ||
      !this.posBuffer ||
      !this.uvBuffer ||
      this.aPos == null ||
      this.aPos < 0 ||
      this.aUv == null ||
      this.aUv < 0
    ) {
      return;
    }

    gl.useProgram(this.program);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, options.tileTexture);
    if (this.uPayload) {
      gl.uniform1i(this.uPayload, 0);
    }

    if (this.uOpacity) {
      gl.uniform1f(this.uOpacity, options.opacity);
    }

    let mode = BinaryDrapedMode.Turbo;
    let lower = options.geoMinMax?.min ?? 0;
    let upper = options.geoMinMax?.max ?? 1;
    let sampleCount = 256;

    const { mapping, usedStale, requestedId, syncState } = options.colormapInfo;

    if (mapping) {
      lower = mapping.recommendedValueRange.lowerBound;
      upper = mapping.recommendedValueRange.upperBound;
      sampleCount = mapping.texture.sampleCount;

      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, mapping.texture.texture(gl));
      if (this.uColormapSampler) {
        gl.uniform1i(this.uColormapSampler, 2);
      }
      mode = BinaryDrapedMode.LUT;

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, options.tileTexture);
      if (this.uPayload) {
        gl.uniform1i(this.uPayload, 0);
      }
    } else {
      const name = (options.colorMapName || "").toLowerCase();
      mode =
        name.includes("gray") || name.includes("grey") || name.includes("mono")
          ? BinaryDrapedMode.Grayscale
          : BinaryDrapedMode.Turbo;
    }

    if (this.uProps2) {
      gl.uniform4f(this.uProps2, lower, upper, sampleCount, 0.0);
    }
    if (this.uMode) {
      gl.uniform1i(this.uMode, mode);
    }

    if (options.debugLoggingEnabled) {
      const debugKey = `${options.parameterUnit}|${options.colorMapName}`;
      if (this.lastMode !== mode || this.lastColormapDebugKey !== debugKey) {
        this.lastMode = mode;
        this.lastColormapDebugKey = debugKey;

        const modeLabel =
          mode === BinaryDrapedMode.LUT
            ? usedStale
              ? "LUT(stale)"
              : "LUT"
            : mode === BinaryDrapedMode.Grayscale
              ? "fallback:grayscale"
              : "fallback:turbo";
        // eslint-disable-next-line no-console
        console.debug(
          `[BinaryDraped] mode=${mode} (${modeLabel}) parameter=${options.parameterUnit} colormap=${
            options.colorMapName
          } colormapState=${String(syncState)} requestedId=${requestedId}`,
        );
      }
    }

    this.updatePosBuffer(gl, options.posCoords ?? { posLeft: -1, posBottom: -1, posRight: 1, posTop: 1 });
    gl.enableVertexAttribArray(this.aPos);
    gl.vertexAttribPointer(this.aPos, 2, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.uvBuffer);
    this.updateUvBuffer(gl, options.texCoords);
    gl.enableVertexAttribArray(this.aUv);
    gl.vertexAttribPointer(this.aUv, 2, gl.FLOAT, false, 0, 0);

    gl.enable(gl.BLEND);
    // The LUT texture is premultiplied, so the globe drape path must blend as premultiplied alpha.
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.disable(gl.DEPTH_TEST);
    gl.depthMask(false);

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    gl.disableVertexAttribArray(this.aPos);
    gl.disableVertexAttribArray(this.aUv);
  }

  private updatePosBuffer(
    gl: WebGL2RenderingContext,
    posCoords: { posLeft: number; posBottom: number; posRight: number; posTop: number },
  ) {
    if (!this.posBuffer) {
      return;
    }
    const { posLeft, posBottom, posRight, posTop } = posCoords;
    const pos = new Float32Array([
      posLeft,
      posBottom,
      posRight,
      posBottom,
      posLeft,
      posTop,
      posLeft,
      posTop,
      posRight,
      posBottom,
      posRight,
      posTop,
    ]);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, pos, gl.DYNAMIC_DRAW);
  }

  private updateUvBuffer(
    gl: WebGL2RenderingContext,
    texCoords: { texLeft: number; texRight: number; texTop: number; texBottom: number },
  ) {
    if (!this.uvBuffer) {
      return;
    }
    const uv = new Float32Array([
      texCoords.texLeft,
      texCoords.texTop,
      texCoords.texRight,
      texCoords.texTop,
      texCoords.texLeft,
      texCoords.texBottom,
      texCoords.texLeft,
      texCoords.texBottom,
      texCoords.texRight,
      texCoords.texTop,
      texCoords.texRight,
      texCoords.texBottom,
    ]);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.uvBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, uv, gl.STATIC_DRAW);
  }
}
